import { useCallback, useMemo } from 'react';

import type { OrderListApiFilters } from '@frontend/api';

import type { SortValue, SortParam } from 'components/listTablePage';

import {
  transformApiToFiltersParams,
  transformFiltersParamsToApi,
} from 'services/orderService';

import { useSearchParams } from 'utils/hooks';

import type { OrderListFilters, OrderListSearchParams } from '../types';

export const useOrderListSearchParams = () => {
  const [searchParams, setSearchParams] =
    useSearchParams<OrderListSearchParams>();

  const { o, page, search, ...filters } = searchParams;

  const activeFilters: OrderListFilters = useMemo(
    () => transformApiToFiltersParams(filters as OrderListApiFilters),
    [filters],
  );

  const isFiltered: boolean = useMemo(
    () =>
      Object.values(activeFilters).some((activeFilterValue) => {
        const filterIsOff =
          !activeFilterValue ||
          (Array.isArray(activeFilterValue) && activeFilterValue.length === 0);

        return !filterIsOff;
      }),
    [activeFilters],
  );

  const isSearched = useMemo(() => !!search, [search]);

  const currentSort = useMemo<SortValue>(() => {
    if (!o) {
      return null;
    }

    if (o.startsWith('-')) {
      return { sortOrder: 'descending', sortKey: o.slice(1) };
    }

    return { sortOrder: 'ascending', sortKey: o };
  }, [o]);

  const setFilters = useCallback(
    (filters: OrderListFilters) => {
      setSearchParams({
        o,
        search,
        ...transformFiltersParamsToApi(filters),
        page: undefined,
      });
    },
    [setSearchParams, o, search],
  );

  const setPage = useCallback(
    (pageNumber: number) => {
      setSearchParams({ ...searchParams, page: pageNumber.toString() });
    },
    [setSearchParams, searchParams],
  );

  const setSearch = useCallback(
    (search?: string) => {
      setSearchParams({
        ...searchParams,
        search: search?.trim() || undefined,
        page: undefined,
      });
    },
    [setSearchParams, searchParams],
  );

  const setSort = useCallback(
    (sortParam: SortParam | null) => {
      setSearchParams({
        ...searchParams,
        o: sortParam ?? undefined,
        page: undefined,
      });
    },
    [setSearchParams, searchParams],
  );

  return {
    searchParams,
    activeFilters,
    isFiltered,
    isSearched,
    currentSort,
    setFilters,
    setPage,
    setSearch,
    setSort,
  } as const;
};
